import { useEffect, useCallback, useRef, useState } from 'react';
import { useAIStream } from './ai/useAIStream';
import { useAIValidation } from './ai/useAIValidation';
import { Question, TriggerRule } from '@/types/theme';
import { supabase } from '@/integrations/supabase/client';
import { formatMessagesForAI, generateSystemPrompt, simulateStreamingText, processMessageContent } from '@/utils/aiUtils';
import { getSupabaseUrl, getSupabaseKey, getEdgeFunctionUrl } from '@/utils/supabaseUtils';
import { webhookLogger } from '@/utils/webhookLogger';
import { triggerExecutor } from '@/utils/triggerExecutor';
// import { generateSystemPrompt as generateAIChatSystemPrompt, useAIChatModeConfig } from '@/hooks/useAIChatModeConfig';

// Import our new modular hooks
import { useConversationState } from './chat/useConversationState';
import { useMessageSystem } from './chat/useMessageSystem';
import { useChatbotSettings } from './chat/useChatbotSettings';
import { useOptionsHandler } from './chat/optionsHandler';
import { useAIDataCollection } from './useAIDataCollection';

// Import types from our new modular system
import {
  UnifiedChatHookReturn,
  UnifiedChatHookProps,
  DEFAULT_BOT_SETTINGS
} from './chat/types';

import { chatLogger } from '@/utils/logger';
import { performanceMonitor, measureAsync } from '@/utils/performance';
import { processPiiVariables, updatePiiChatReferences, updatePiiChatReferencesNew, getPreviousAnswers } from '@/utils/piiUtils';
import { isPhoneQuestion } from '@/utils/phoneValidation';
import { 
  getFieldsDictionaryAndUserInfo,
  type UserInfoItem,
  type FieldsAndUserInfoResult 
} from '@/utils/chatDataUtils';


/**
 * Unified Chat Hook - Refactored Implementation
 *
 * This is the main, production-ready unified chat hook implementation.
 * Breaking down the original large hook into focused, composable pieces.
 *
 * This version is significantly smaller and more maintainable by:
 * - Delegating state management to specialized hooks
 * - Using composition over large monolithic functions
 * - Better separation of concerns
 * - Improved error handling and logging
 * - Enhanced performance monitoring
 *
 * For legacy version see: useUnifiedChat.legacy.ts
 */

interface UseUnifiedChatProps {
  chatbotId?: string;
  questions?: Question[];
  welcomeMessage?: string;
  completionMessage?: string;
  messageDelay?: number;
  buttonOptionsDelay?: number;
  noResponseDelay?: number;
  optionsHandlerDelay?: number;
  validationConfig?: {
    enabled?: boolean;
    maxAttempts?: number;
    strictValidation?: boolean;
  };
  onWebhookSent?: (data: any) => void;
  triggerRules?: TriggerRule[];
  piiData?: any; // Add piiData prop
  piiDataToken?: string; // Add piiToken prop
}

export function useUnifiedChat({
  chatbotId,
  questions = [],
  welcomeMessage = 'Welcome! How can I help you today?',
  completionMessage = 'Thank you for chatting with us!',
  messageDelay = 1000,
  buttonOptionsDelay = 1000,
  noResponseDelay = 1000,
  optionsHandlerDelay = 1000,
  validationConfig = { enabled: false, maxAttempts: 2, strictValidation: false },
  onWebhookSent,
  triggerRules = [],
  piiData, // Add piiData prop
  piiDataToken // Add piiToken prop
}: UseUnifiedChatProps) {
  // Use specialized hooks for different concerns
  const conversationState = useConversationState();
  const messageSystem = useMessageSystem();
  const chatbotSettings = useChatbotSettings();
  const { processAIDataCollection } = useAIDataCollection();
  // const aiChatConfig = useAIChatModeConfig(chatbotId || '');

  // Trigger rules state
  const [triggerRulesState, setTriggerRulesState] = useState<TriggerRule[]>(triggerRules);

  const urlParams = new URLSearchParams(window.location.search);
  const piiToken = piiDataToken || urlParams.get('pii_token') || urlParams.get('pii');

  useEffect(() => {
    setTriggerRulesState(triggerRules);
  }, [triggerRules, chatbotId]);

  const { showOptionsAfterQuestion } = useOptionsHandler(
    questions,
    conversationState.currentQuestionIndex,
    messageSystem.setIsTyping,
    messageSystem.messages
  );

  // Reference to store the current message ID being updated
  const currentStreamingMessageId = useRef<string | null>(null);

  // Track the last processed question to prevent duplicate processing
  const lastProcessedQuestionIndexRef = useRef(-1);

  // Store fields dictionary and user info for reuse across the component
  const fieldsDictionaryRef = useRef<FieldsAndUserInfoResult['fieldsDictionary']>([]);
  const userInfoRef = useRef<FieldsAndUserInfoResult['userInfo']>([]);
  const piiTokenRef = useRef<FieldsAndUserInfoResult['piiToken']>(null);
  const dataLoadedRef = useRef<boolean>(false);

  // Function to load/refresh fields dictionary and user info
  const loadFieldsAndUserInfo = useCallback(async () => {
    if (!chatbotId || !conversationState.conversationId.current || !conversationState.sessionId.current) {
      return;
    }
    
    try {
      const result = await getFieldsDictionaryAndUserInfo(
        conversationState.conversationId.current,
        chatbotId,
        conversationState.sessionId.current
      );
      fieldsDictionaryRef.current = result.fieldsDictionary;
      userInfoRef.current = result.userInfo;
      piiTokenRef.current = result.piiToken;
      dataLoadedRef.current = true;
      
    } catch (error) {
      chatLogger.error('Error loading fields dictionary and user info', { error });
    }
  }, [chatbotId, conversationState.conversationId, conversationState.sessionId]);

  // Load fields and user info when conversation is initialized
  useEffect(() => {
    if (conversationState.conversationId.current && chatbotId && !dataLoadedRef.current) {
      loadFieldsAndUserInfo();
    }
  }, [conversationState.conversationId.current, chatbotId, loadFieldsAndUserInfo]);

  // AI capabilities with improved error handling
  const {
    streamResponse,
    isStreaming
  } = useAIStream({
    onStart: () => {
      messageSystem.setIsTyping(true);
      chatLogger.ai('AI stream started');
    },
    onComplete: async (fullText) => {
      messageSystem.setIsTyping(false);
      chatLogger.ai('AI stream completed', { textLength: fullText.length });

      // Save the final AI response to the database
      if (currentStreamingMessageId.current && fullText.trim()) {
        messageSystem.updateMessage(currentStreamingMessageId.current, fullText, true, true); // Explicitly mark as bot message
        chatLogger.ai('Final AI response saved to database', {
          messageId: currentStreamingMessageId.current,
          textLength: fullText.length
        });
        // Note: processAIDataCollection is already called in getAIResponse, no need to call it again here
      }
    },
    onToken: (_, accumulated) => {
      if (currentStreamingMessageId.current) {
        messageSystem.updateMessage(currentStreamingMessageId.current, accumulated, false);
      }
    },
    onError: (error) => {
      chatLogger.error('AI stream error', { error: error.message });
      messageSystem.setIsTyping(false);
    }
  });

  const { validateResponse, isValidating } = useAIValidation();

  // Load chatbot settings when chatbotId changes
  useEffect(() => {
    if (chatbotId) {
      chatbotSettings.loadSettings(chatbotId);
    }
  }, [chatbotId, chatbotSettings.loadSettings]);

  // Initialize database conversation when chatbotId or piiToken changes
  useEffect(() => {
    if (chatbotId) {
      const existingPiiToken = piiToken || null;
      conversationState.initializeDbConversation(chatbotId, existingPiiToken);
    }
  }, [chatbotId, conversationState.initializeDbConversation, piiToken]);

  // Update message system with conversation ID when it changes
  useEffect(() => {
    if (conversationState.conversationId.current) {
      messageSystem.setConversationId(conversationState.conversationId.current);
    }
  }, [conversationState.conversationId.current, messageSystem.setConversationId]);

  // Present the next question in the flow
  const presentNextQuestion = useCallback(async (targetQuestionIndex?: number) => {
    const questionIndex = targetQuestionIndex ?? conversationState.currentQuestionIndex;

    chatLogger.flow('Presenting question', {
      questionIndex,
      questionsLength: questions.length
    });

    // Guard against duplicate processing
    if (lastProcessedQuestionIndexRef.current === questionIndex) {
      return;
    }

    // Track that we're processing this question
    lastProcessedQuestionIndexRef.current = questionIndex;

    // Check if flow is completed
    if (questionIndex >= questions.length) {
      chatLogger.flow('Flow completed', {
        questionIndex,
        questionsLength: questions.length
      });
      conversationState.setFlowCompleted(true);

      // Trigger webhook if configured
      handleWebhookSending();

      // Execute trigger rules on conversation completion
      executeTriggerRules();

      return;
    }

    const currentQuestion = questions[questionIndex];
    if (!currentQuestion) {
      chatLogger.error('Question not found at index', {
        questionIndex,
        questionsLength: questions.length
      });
      return;
    }

    chatLogger.flow('Presenting question', {
      questionIndex,
      questionId: currentQuestion.id,
      questionType: currentQuestion.type
    });

    // Process PII variables in question text in real-time
    let processedQuestionText = processPiiVariables(currentQuestion.text, piiData);

    // Process rich media content if available
    if (currentQuestion.rich_content?.enabled && currentQuestion.rich_content?.media_elements?.length > 0) {
      processedQuestionText = processMessageContent(processedQuestionText, currentQuestion.rich_content);
    }

    chatLogger.flow('PII processing for question', {
      originalText: currentQuestion.text.substring(0, 50) + '...',
      processedText: processedQuestionText.substring(0, 50) + '...',
      hasPiiData: !!piiData,
      piiKeys: piiData ? Object.keys(piiData) : []
    });

    // Special handling for no_response question type
    if (currentQuestion.type === 'no_response') {
      messageSystem.setIsTyping(true);

      setTimeout(async () => {
        // Process rich media for no_response questions too
        let finalProcessedText = processedQuestionText;
        if (currentQuestion.rich_content?.enabled && currentQuestion.rich_content?.media_elements?.length > 0) {
          finalProcessedText = processMessageContent(processedQuestionText, currentQuestion.rich_content);
        }

        const messageId = await messageSystem.addMessage(
          finalProcessedText, // Use fully processed text
          'bot',
          {
            questionId: currentQuestion.id,
            questionType: currentQuestion.type,
            questionText: finalProcessedText, // Store processed text in metadata
            questionOptions: currentQuestion.options || []
          }
        );

        messageSystem.setIsTyping(false);

        setTimeout(() => {
          conversationState.addUserResponse(
            currentQuestion.shortname || currentQuestion.id,
            'auto-progressed'
          );

          const currentIndex = conversationState.currentQuestionIndex;
          const nextIndex = currentIndex + 1;

          if (nextIndex < questions.length) {
            conversationState.setCurrentQuestionIndex(nextIndex);
            chatLogger.flow('No response auto-progression completed', {
              newIndex: nextIndex,
              totalQuestions: questions.length
            });
          } else {
            conversationState.setFlowCompleted(true);

            if (completionMessage) {
              messageSystem.setIsTyping(true);
              setTimeout(async () => {
                await messageSystem.addMessage(completionMessage, 'bot');
                messageSystem.setIsTyping(false);
                conversationState.setConversationState('completed');

                // Send webhook if configured
                if (onWebhookSent) {
                  handleWebhookSending();
                }
              }, noResponseDelay); // Use configurable delay for no_response questions

            }
          }
        }, 2000); // 2 second delay to allow users to read the message

      }, noResponseDelay); // Use configurable delay for no_response questions

      return; // Exit early for no_response questions
    }

    messageSystem.setIsTyping(true);

    setTimeout(async () => {
      const messageMetadata = {
        questionId: currentQuestion.id,
        questionType: currentQuestion.type,
        questionText: processedQuestionText, // Use processed text in metadata
        questionOptions: currentQuestion.options || [],
        chatbot_id: currentQuestion.chatbot_id,
        shortname: currentQuestion.shortname,
        errormessage: currentQuestion.errormessage,
        responsevalidation: currentQuestion.responsevalidation,
        validation_rules: currentQuestion.validation_rules,
        validationType: currentQuestion.validationType,
        created_at: currentQuestion.created_at,
        updated_at: currentQuestion.updated_at,
        placeholder: currentQuestion.placeholder,
        order: currentQuestion.order
      };

      const messageId = await messageSystem.addMessage(processedQuestionText, 'bot', messageMetadata); // Use processed text for display
      
      // Check if this is a phone question and show TCPA warning if needed
      const isPhoneQuestionCheck = isPhoneQuestion(currentQuestion.text, currentQuestion.type);
      if (isPhoneQuestionCheck && chatbotSettings.botSettings.tcpaLanguage) {
        // Show TCPA warning after a brief delay
        setTimeout(async () => {
          const tcpaMessage = `⚠️ **TCPA Notice**\n\n${chatbotSettings.botSettings.tcpaLanguage}`;
          await messageSystem.addMessage(tcpaMessage, 'bot', {
            isTcpaWarning: true,
            relatedQuestionId: currentQuestion.id
          });
        }, 500); // Small delay to ensure the question appears first
      }
      
      messageSystem.setIsTyping(false);

      const hasExplicitOptions = currentQuestion.options && currentQuestion.options.length > 0;
      const isBooleanQuestion = currentQuestion.type === 'boolean';
      const isButtonQuestion = currentQuestion.type === 'button';
      const isSelectQuestion = currentQuestion.type === 'select';

      if (hasExplicitOptions || isBooleanQuestion || (isButtonQuestion && !hasExplicitOptions) || (isSelectQuestion && !hasExplicitOptions)) {
        setTimeout(() => {
          showOptionsAfterQuestion(messageId, currentQuestion.id);
        }, buttonOptionsDelay);
      }
    }, messageDelay);
  }, [
    conversationState.currentQuestionIndex,
    questions,
    conversationState.setFlowCompleted,
    completionMessage,
    messageSystem,
    chatbotSettings.botSettings.messageDelay,
    conversationState.setConversationState,
    showOptionsAfterQuestion
  ]);

  // Enhanced trigger rules execution - checks conditions and executes immediately
  const checkAndExecuteTriggerRules = useCallback(async (additionalResponses?: Record<string, string>) => {
    if (!chatbotId || triggerRulesState.length === 0) {
      return;
    }

    try {
      // Merge current responses with any additional responses passed in
      const mergedResponses = {
        ...conversationState.userResponses,
        ...(additionalResponses || {})
      };

      const context = {
        chatbotId,
        sessionId: conversationState.sessionId.current,
        conversationId: conversationState.conversationId.current, // Add conversation ID for proper linking
        userResponses: mergedResponses,
        messages: messageSystem.messages,
        conversationState: conversationState,
        questions: questions, // Add questions for POST variable mapping
        piiData // Add piiData to context
      };

      // Execute trigger rules - this will evaluate conditions and fire matching triggers
      await triggerExecutor.evaluateAndExecuteTriggers(triggerRulesState, context);
    } catch (error) {
      chatLogger.error('Failed to check and execute trigger rules', { error });
    }
  }, [chatbotId, triggerRulesState, conversationState.sessionId, conversationState.userResponses, messageSystem.messages, conversationState.currentQuestionIndex]);

  // Update PII with chat references
  const updatePiiWithChatReferences = useCallback(async () => {
    // Check if we have a PII token and chatbot ID
    if (!piiToken || !chatbotId || Object.keys(conversationState.userResponses).length === 0) {
      return;
    }

    try {
      // Update PII data with current chat references
      // const result = await updatePiiChatReferences(
      //   piiToken,
      //   chatbotId,
      //   conversationState.userResponses
      // );
      const result = await updatePiiChatReferencesNew(
          conversationState.conversationId.current!,
          conversationState.userResponses,
          piiToken,
      );

      if (!result.success) {
        chatLogger.error('Failed to update PII chat references', { error: result.error });
      }
    } catch (error) {
      chatLogger.error('Error updating PII chat references', { error });
    }
  }, [chatbotId, conversationState.userResponses]);

  // Handle user responses
  const handleUserResponse = useCallback(async (response: string) => {
    chatLogger.flow('Handling user response', {
      response: response.substring(0, 50) + '...',
      currentQuestionIndex: conversationState.currentQuestionIndex,
      questionsLength: questions.length,
      chatMode: chatbotSettings.botSettings.chatMode || 'ai_only'
    });

    // Check chat mode to determine routing
    const chatMode = chatbotSettings.botSettings.chatMode || 'ai_only';

    // If AI-only mode or flow is completed, route to AI response
    if (chatMode === 'ai_only' || conversationState.flowCompleted) {
      chatLogger.flow('Routing to AI response', { chatMode, flowCompleted: conversationState.flowCompleted });
      
      // In AI-only mode, save user message as response (matching question flow behavior)
      if (chatMode === 'ai_only' && conversationState.conversationId.current) {
        // Create a virtual question for the user message
        const virtualQuestion = {
          id: `ai_user_message_${Date.now()}`,
          chatbot_id: chatbotId || '',
          text: 'User Message',
          type: 'text' as const,
          order: 0,
          shortname: 'user_message',
          options: null,
          errormessage: '',
          responsevalidation: '',
          validation_rules: {},
          validationType: 'none' as const,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        // Save the user message using the same mechanism as question flow
        conversationState.addUserResponse(virtualQuestion.id, response, virtualQuestion);
      }
      
      await getAIResponse(response);
      // Note: processAIDataCollection is already called inside getAIResponse, no need to call it again here
      
      return;
    }

    // Question flow mode - continue with structured flow
    // Add user message
    await messageSystem.addMessage(response, 'user', undefined);

    // Store the response if we have a current question
    let currentQuestionId: string | null = null;
    if (conversationState.currentQuestionIndex < questions.length) {
      const currentQuestion = questions[conversationState.currentQuestionIndex];
      if (currentQuestion) {
        currentQuestionId = currentQuestion.id;
        conversationState.addUserResponse(currentQuestion.id, response, currentQuestion);

        // Update PII with chat references after each response
        await updatePiiWithChatReferences();
      }
    }

    // Check if current question has AI response enabled
    if (conversationState.currentQuestionIndex < questions.length) {
      const currentQuestion = questions[conversationState.currentQuestionIndex];
      if (currentQuestion?.enable_ai_response) {
        chatLogger.ai('Question has AI response enabled', {
          questionId: currentQuestion.id,
          questionText: currentQuestion.text.substring(0, 50) + '...',
          aiResponsePrompt: currentQuestion.ai_response_prompt
        });

        // Add a typing indicator
        messageSystem.setIsTyping(true);
        
        // Generate AI response
        setTimeout(async () => {
          try {
            // Create a bot message for the AI response
            const botMessageId = await messageSystem.addMessage('...', 'bot', undefined);
            currentStreamingMessageId.current = botMessageId;
            messageSystem.setMessageStreaming(botMessageId, true);

            // Build context for AI response
            const systemPrompt = currentQuestion.ai_response_prompt 
              ? `You are a helpful assistant. ${currentQuestion.ai_response_prompt}\n\nThe user just answered the question: "${currentQuestion.text}" with: "${response}". Provide a helpful and contextual response.`
              : `You are a helpful assistant. The user just answered the question: "${currentQuestion.text}" with: "${response}". Acknowledge their answer and provide a helpful, conversational response that shows you understood their answer.`;

            // Get recent conversation context
            const recentMessages = messageSystem.messages.slice(-6); // Last 3 exchanges
            const aiMessages = formatMessagesForAI(recentMessages);

            await streamResponse(
              [
                { role: 'system', content: systemPrompt },
                ...aiMessages
              ],
              chatbotId
            );
            
            messageSystem.setMessageStreaming(botMessageId, false);
            messageSystem.setIsTyping(false);
            currentStreamingMessageId.current = null;
            chatLogger.ai('AI response completed for question', { questionId: currentQuestion.id });
          } catch (error) {
            chatLogger.error('Failed to generate AI response', {
              error: error instanceof Error ? error.message : 'Unknown error',
              questionId: currentQuestion.id
            });
            messageSystem.setIsTyping(false);
          }
        }, chatbotSettings.botSettings.messageDelay);
        
        // Wait a bit longer before moving to next question to allow AI response to complete
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    // Check and execute trigger rules after each response
    // Pass the current response directly to avoid state timing issues
    if (currentQuestionId) {
      const currentResponse = { [currentQuestionId]: response };
      await checkAndExecuteTriggerRules(currentResponse);
    } else {
      await checkAndExecuteTriggerRules();
    }

    // Move to next question or complete flow
    if (conversationState.currentQuestionIndex < questions.length - 1) {
      const nextIndex = conversationState.currentQuestionIndex + 1;
      conversationState.setCurrentQuestionIndex(nextIndex);

      // Reset the guard to allow processing the next question
      lastProcessedQuestionIndexRef.current = -1;

      setTimeout(() => {
        presentNextQuestion(nextIndex);
      }, optionsHandlerDelay);
    } else {
      // Flow completed, handle final response
      conversationState.setFlowCompleted(true);

      // Complete the database conversation
      conversationState.completeDbConversation();

      if (completionMessage) {
        messageSystem.setIsTyping(true);
        setTimeout(async () => {
          await messageSystem.addMessage(completionMessage, 'bot');
          messageSystem.setIsTyping(false);
          conversationState.setConversationState('completed');

          // Send webhook if configured
          if (onWebhookSent) {
            handleWebhookSending();
          }
        }, chatbotSettings.botSettings.messageDelay);
      }
    }
  }, [
    conversationState,
    questions,
    messageSystem,
    presentNextQuestion,
    completionMessage,
    chatbotSettings.botSettings.messageDelay,
    onWebhookSent,
    checkAndExecuteTriggerRules
  ]);

  // Handle webhook sending with comprehensive logging
  const handleWebhookSending = useCallback(async () => {
    try {
      // Get chatbot settings to check for webhook configuration
      const { data: chatbotData, error } = await supabase
        .from('chatbots')
        .select('id, name, settings')
        .eq('id', chatbotId)
        .single();

      if (error || !chatbotData) {
        chatLogger.error('Failed to fetch chatbot for webhook', { error: error?.message });
        return;
      }

      // Check if webhook is configured in settings
      const webhookUrl = chatbotData.settings?.webhookUrl;
      if (!webhookUrl) {
        chatLogger.conversation('No webhook configured for chatbot', { chatbotId });
        return;
      }

      chatLogger.conversation('Webhook trigger initiated', {
        sessionId: conversationState.sessionId.current,
        chatbotId,
        webhookUrl: chatbotData.settings.webhookUrl
      });

      // Prepare webhook payload
      const webhookPayload = {
        sessionId: conversationState.sessionId.current,
        chatbotId,
        chatbotName: chatbotData.name,
        responses: conversationState.userResponses,
        messages: messageSystem.messages,
        completedAt: new Date().toISOString(),
        totalQuestions: questions.length,
        responseCount: Object.keys(conversationState.userResponses).length,
        piiData // Add piiData to webhook payload
      };

      // Get PII data if available
      let piiDataForWebhook = null;
      try {
        const piiResponse = await fetch(`/api/pii/${conversationState.sessionId.current}`);
        if (piiResponse.ok) {
          piiDataForWebhook = await piiResponse.json();
        }
      } catch (piiError) {
        chatLogger.error('Could not fetch PII data for webhook', {
          sessionId: conversationState.sessionId.current,
          error: piiError instanceof Error ? piiError.message : 'Unknown error'
        });
      }

      // Send webhook with comprehensive logging
      const result = await measureAsync('webhook-send', async () => {
        return await webhookLogger.sendWebhookWithLogging(
          chatbotId,
          conversationState.sessionId.current,
          webhookUrl,
          webhookPayload,
          piiDataForWebhook,
          'flow_completion'
        );
      });

      chatLogger.conversation('Webhook logging successful', {
        sessionId: conversationState.sessionId.current,
        webhookUrl: chatbotData.settings.webhookUrl,
        logId: conversationState.sessionId.current
      });

      // Call the original callback if provided
      if (onWebhookSent) {
        onWebhookSent({
          ...webhookPayload,
          webhookResult: result,
          piiData // Add piiData to callback
        });
      }

      chatLogger.conversation('Webhook processing completed', {
        sessionId: conversationState.sessionId.current,
        success: result.success,
        status: result.status,
        executionTime: result.executionTime
      });

    } catch (error) {
      chatLogger.error('Failed to process webhook', {
        error: error instanceof Error ? error.message : 'Unknown error',
        sessionId: conversationState.sessionId.current,
        chatbotId
      });
    }
  }, [chatbotId, conversationState, messageSystem.messages, questions.length, onWebhookSent]);

  // Get AI response for open-ended conversations
  const getAIResponse = useCallback(async (userInput: string) => {
    if (!chatbotId) {
      chatLogger.error('Cannot get AI response without chatbotId');
      return;
    }

    try {
      chatLogger.ai('Getting AI response', {
        userInput: userInput.substring(0, 50) + '...',
        chatbotId
      });

      // Check for conversation management triggers in AI-only mode
      const chatMode = chatbotSettings.botSettings.chatMode || 'ai_only';
      if (chatMode === 'ai_only') {
        try {
          const { data: chatbot } = await supabase
            .from('chatbots')
            .select('ai_settings')
            .eq('id', chatbotId)
            .single();

          if (chatbot?.ai_settings && (chatbot.ai_settings as any).ai_chat_config) {
            const config = (chatbot.ai_settings as any).ai_chat_config;
            const lowerInput = userInput.toLowerCase();

            // Check for end conversation triggers
            if (config.endConversationTriggers?.some(trigger =>
              lowerInput.includes(trigger.toLowerCase())
            )) {
              await messageSystem.addMessage(userInput, 'user', undefined);
              await messageSystem.addMessage(
                'Thank you for chatting with me! Have a great day!',
                'bot',
                undefined
              );
              return;
            }

            // Check for escalation triggers
            if (config.escalationTriggers?.some(trigger =>
              lowerInput.includes(trigger.toLowerCase())
            )) {
              await messageSystem.addMessage(userInput, 'user', undefined);
              await messageSystem.addMessage(
                'I understand you\'d like to speak with a human representative. Let me connect you with someone who can better assist you.',
                'bot',
                undefined
              );
              return;
            }
          }
        } catch (error) {
          console.warn('Error checking conversation triggers:', error);
        }
      }

      // Ensure fields dictionary and user info are loaded
      if (!dataLoadedRef.current) {
        await loadFieldsAndUserInfo();
      }
      // Use refs for fieldsDictionary and userInfo
      const fieldsDictionary = fieldsDictionaryRef.current;
      const userInfo = userInfoRef.current;
      const piiTokenInfo = piiTokenRef.current;

      // First, add the user's message to the chat
      const userMessageId = await messageSystem.addMessage(userInput, 'user', undefined);
      chatLogger.ai('Added user message for AI response', { userMessageId, userInput: userInput.substring(0, 50) + '...' });

      // Process AI data collection immediately after user message is saved
      // This ensures data capture even if AI streaming fails
      // Determine if we should trigger AI data collection
      // This should happen for:
      // 1. Pure AI-only mode (chatMode === 'ai_only')
      // 2. Post-flow AI responses in question_flow mode (when getAIResponse is called)
      // 3. Valid AI chat configuration exists for data extraction
      
      // Temporarily disable AI config check to test the core flow
      const shouldTriggerAIDataCollection = conversationState.conversationId.current && chatbotId;
      
      if (shouldTriggerAIDataCollection) {
        try {
          // Get AI chat config - Use settings field
          const { data: chatbot } = await supabase
            .from('chatbots')
            .select('settings')
            .eq('id', chatbotId)
            .single();

          const parsedSettings = typeof chatbot?.settings === 'string' 
            ? JSON.parse(chatbot.settings) 
            : chatbot?.settings;

          if (parsedSettings?.ai_chat_config) {
            const updatedUserInfo = await processAIDataCollection(
              conversationState.conversationId.current,
              chatbotId,
              conversationState.sessionId.current,
              parsedSettings.ai_chat_config,
              fieldsDictionary,
              userInfo
            );
            
            // Update userInfo ref if new data was returned
            if (updatedUserInfo) {
              userInfoRef.current = updatedUserInfo;
            }
          }
        } catch (error) {
          chatLogger.error('AI Data Collection error', { error });
        }
      }

      // Then create a bot message for the AI response with placeholder text
      const botMessageId = await messageSystem.addMessage('...', 'bot', undefined);
      currentStreamingMessageId.current = botMessageId;
      messageSystem.setMessageStreaming(botMessageId, true);

      // Get the current messages (including the user message we just added)
      let aiMessages = formatMessagesForAI(messageSystem.messages);

      // Apply context memory and conversation length limits for AI-only mode
      if (chatMode === 'ai_only') {
        try {
          const { data: chatbot } = await supabase
            .from('chatbots')
            .select('ai_settings')
            .eq('id', chatbotId)
            .single();

          if (chatbot?.ai_settings && (chatbot.ai_settings as any).ai_chat_config) {
            const config = (chatbot.ai_settings as any).ai_chat_config;

            // Apply conversation length limit
            if (config.maxConversationLength && aiMessages.length > config.maxConversationLength) {
              // Keep only the most recent messages within the limit
              aiMessages = aiMessages.slice(-config.maxConversationLength);
              chatLogger.ai('Applied conversation length limit', {
                originalLength: messageSystem.messages.length,
                limitedLength: aiMessages.length,
                maxLength: config.maxConversationLength
              });
            }

            // If context memory is disabled, only keep the current user message
            if (!config.contextMemory) {
              // Keep only the current user message
              aiMessages = aiMessages.slice(-1);
              chatLogger.ai('Context memory disabled, using only current message');
            }
          }
        } catch (error) {
          console.warn('Error applying conversation limits:', error);
        }
      }

      // Get AI configuration and generate comprehensive system prompt
      let systemPrompt = generateSystemPrompt('IntentDrive Assistant', 'IntentDrive', []);
      
      // If in AI-only mode, use the comprehensive AI configuration
      // (chatMode already declared above)
      if (chatMode === 'ai_only') {
        try {
          const { data: chatbot, error: chatbotError } = await supabase
            .from('chatbots')
            .select('ai_settings, settings, name, company_name')
            .eq('id', chatbotId)
            .single();
            
          // Check both ai_settings and settings for AI config
          let aiChatConfig = null;
          
          // Parse settings if it's a string
          let parsedSettings = chatbot?.settings;
          if (typeof parsedSettings === 'string') {
            try {
              parsedSettings = JSON.parse(parsedSettings);
            } catch (e) {
              console.error('Error parsing settings:', e);
              parsedSettings = null;
            }
          }
          
          if (chatbot?.ai_settings && (chatbot.ai_settings as any).ai_chat_config) {
            aiChatConfig = (chatbot.ai_settings as any).ai_chat_config;
          } else if (parsedSettings && (parsedSettings as any).ai_chat_config) {
            aiChatConfig = (parsedSettings as any).ai_chat_config;
          }

          if (aiChatConfig) {
            // Import the comprehensive system prompt generator
            const { generateSystemPrompt: generateAISystemPrompt } = await import('@/hooks/useAIChatModeConfig');

            // Use fieldsDictionary and userInfo that were already fetched at the beginning of getAIResponse
            // Generate comprehensive system prompt with AI configuration
            let aiSystemPrompt = await generateAISystemPrompt(
                aiChatConfig,
                fieldsDictionary,
                userInfo,
                piiTokenInfo
            );
            
            // Add company context
            if (chatbot.name || chatbot.company_name) {
              aiSystemPrompt = `You are representing ${chatbot.name || 'this company'}${chatbot.company_name ? ` at ${chatbot.company_name}` : ''}.\n\n${aiSystemPrompt}`;
            }

            // Add knowledge base context if available
            try {
              const { data: knowledgeEntries } = await supabase
                .from('knowledge_base')
                .select('content')
                .eq('chatbot_id', chatbotId);

              if (knowledgeEntries && knowledgeEntries.length > 0) {
                const knowledgeContext = knowledgeEntries.map(entry => entry.content).join('\n\n');
                aiSystemPrompt += `\n\nKnowledge Base:\nUse the following information to answer questions when relevant:\n\n${knowledgeContext}`;
              }
            } catch (error) {
              console.warn('Could not load knowledge base:', error);
            }

            systemPrompt = aiSystemPrompt;
            
            chatLogger.ai('Using comprehensive AI configuration for system prompt', {
              hasPersonality: !!aiChatConfig.aiPersonality,
              hasInstructions: !!aiChatConfig.systemInstructions,
            });
          }
        } catch (error) {
          console.error('Error loading AI configuration, using default prompt:', error);
        }
      }

      await streamResponse(
        [
          { role: 'system', content: systemPrompt },
          ...aiMessages,
          { role: 'user', content: userInput }
        ],
        systemPrompt,
        chatbotId
      );

      messageSystem.setMessageStreaming(botMessageId, false);
      currentStreamingMessageId.current = null;

    } catch (error) {
      chatLogger.error('AI response failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        userInput: userInput.substring(0, 50) + '...'
      });

      messageSystem.setIsTyping(false);
      if (currentStreamingMessageId.current) {
        messageSystem.updateMessage(
          currentStreamingMessageId.current,
          'Sorry, I encountered an error. Please try again.',
          true, // Save error message to database
          true  // Explicitly mark as bot message
        );
        messageSystem.setMessageStreaming(currentStreamingMessageId.current, false);
      }
    }
  }, [
    chatbotId,
    messageSystem,
    streamResponse,
    conversationState
  ]);

  // Legacy trigger execution for conversation completion (still useful for completion-based triggers)
  const executeTriggerRules = useCallback(async () => {
    if (!chatbotId || triggerRulesState.length === 0) {
      return;
    }

    try {
      const context = {
        chatbotId,
        sessionId: conversationState.sessionId.current,
        conversationId: conversationState.conversationId.current, // Add conversation ID for proper linking
        userResponses: conversationState.userResponses,
        messages: messageSystem.messages,
        conversationState: conversationState,
        questions: questions, // Add questions for POST variable mapping
        piiData // Add piiData to context
      };

      await triggerExecutor.evaluateAndExecuteTriggers(triggerRulesState, context);
    } catch (error) {
      chatLogger.error('Failed to execute trigger rules', { error });
    }
  }, [chatbotId, triggerRulesState, conversationState.sessionId, conversationState.userResponses, messageSystem.messages]);

  // Track if previous answers have been loaded to prevent re-fetching
  const previousAnswersLoadedRef = useRef(false);

  // Load previous answers from other chatbots if PII token is provided
  const loadPreviousAnswers = useCallback(async () => {
    // Prevent multiple loads
    if (previousAnswersLoadedRef.current) {
      return;
    }

    if (!piiToken || !chatbotId) {
      return;
    }

    try {
      previousAnswersLoadedRef.current = true; // Mark as loading/loaded

      // Get previous answers from other chatbots (pass empty object for initial load)
      const previousAnswers = await getPreviousAnswers(
        piiToken,
        chatbotId,
        {} // Don't pass current answers to avoid circular updates
      );

      if (previousAnswers && Object.keys(previousAnswers).length > 0) {
        // Merge previous answers into current conversation state
        Object.entries(previousAnswers).forEach(([questionId, answer]) => {
          // Convert to string since addUserResponse expects string
          const answerString = answer != null ? String(answer) : '';
          if (answerString && answerString !== '[object Object]') {
            conversationState.addUserResponse(questionId, answerString);
          }
        });

      }
    } catch (error) {
      chatLogger.error('Error loading previous answers', { error });
      previousAnswersLoadedRef.current = false; // Allow retry on error
    }
  }, [piiToken, chatbotId, conversationState.addUserResponse]);

  // Load previous answers from other chatbots when PII token is available
  // useEffect(() => {
  //   if (piiToken && chatbotId && conversationState.conversationId.current && !previousAnswersLoadedRef.current) {
  //     loadPreviousAnswers();
  //   }
  // }, [piiToken, chatbotId, conversationState.conversationId.current, loadPreviousAnswers]);

  // Effect to trigger presentNextQuestion when currentQuestionIndex changes
  useEffect(() => {
    if (conversationState.conversationState === 'welcome_shown' && 
        conversationState.currentQuestionIndex >= 0 && 
        questions.length > 0 && 
        !conversationState.flowCompleted) {
      presentNextQuestion(conversationState.currentQuestionIndex);
    }
  }, [
    conversationState.currentQuestionIndex, 
    conversationState.conversationState,
    conversationState.flowCompleted,
    questions.length,
    presentNextQuestion
  ]);

  // Start conversation
  const startConversation = useCallback(async () => {
    if (conversationState.conversationState !== 'initial') {
      return;
    }

    chatLogger.conversation('Starting conversation', {
      hasWelcomeMessage: !!welcomeMessage,
      questionsCount: questions.length,
      sessionId: conversationState.sessionId.current
    });

    conversationState.setConversationState('in_progress');

    if (welcomeMessage) {
      messageSystem.setIsTyping(true);
      setTimeout(async () => {
        await messageSystem.addMessage(welcomeMessage, 'bot', undefined);
        messageSystem.setIsTyping(false);
        conversationState.setConversationState('welcome_shown');

        // Check chat mode to determine flow
        const chatMode = chatbotSettings.botSettings.chatMode || 'ai_only';

        if (chatMode === 'ai_only' || questions.length === 0) {
          // AI-only mode or no questions - enable free-form conversation
          conversationState.setFlowCompleted(true);
          conversationState.setConversationState('completed');
          chatLogger.conversation('Started AI-only conversation mode', {
            chatMode,
            questionsCount: questions.length
          });
        } else {
          // Question flow mode - present the first question
          setTimeout(() => {
            // Call presentNextQuestion indirectly to avoid circular dependency
            if (questions.length > 0) {
              conversationState.setCurrentQuestionIndex(0);
            }
          }, chatbotSettings.botSettings.messageDelay);
          chatLogger.conversation('Started question flow mode', {
            chatMode,
            questionsCount: questions.length
          });
        }
      }, chatbotSettings.botSettings.messageDelay);
    } else {
      conversationState.setConversationState('welcome_shown');

      // Check chat mode to determine flow (no welcome message case)
      const chatMode = chatbotSettings.botSettings.chatMode || 'ai_only';

      if (chatMode === 'ai_only' || questions.length === 0) {
        // AI-only mode or no questions - enable free-form conversation
        conversationState.setFlowCompleted(true);
        conversationState.setConversationState('completed');
        chatLogger.conversation('Started AI-only conversation mode (no welcome)', {
          chatMode,
          questionsCount: questions.length
        });
      } else {
        // Question flow mode - present the first question
        setTimeout(() => {
          // Call presentNextQuestion indirectly to avoid circular dependency
          if (questions.length > 0) {
            conversationState.setCurrentQuestionIndex(0);
          }
        }, 100);
        chatLogger.conversation('Started question flow mode (no welcome)', {
          chatMode,
          questionsCount: questions.length
        });
      }
    }
  }, [
    conversationState.conversationState,
    conversationState.setConversationState,
    conversationState.setFlowCompleted,
    welcomeMessage,
    questions.length,
    messageSystem.addMessage,
    messageSystem.setIsTyping,
    chatbotSettings.botSettings.messageDelay,
    chatbotSettings.botSettings.chatMode
  ]);

  // Reset conversation
  const resetConversation = useCallback(() => {
    chatLogger.conversation('Resetting conversation');

    conversationState.resetConversation();
    messageSystem.clearMessages();
    currentStreamingMessageId.current = null;

    // Start new conversation after reset
    setTimeout(() => startConversation(), 100);
  }, [conversationState.resetConversation, messageSystem.clearMessages, startConversation]);

  return {
    // State
    messages: messageSystem.messages,
    isTyping: messageSystem.isTyping || isStreaming || isValidating,
    flowCompleted: conversationState.flowCompleted,
    streamingMessageIds: messageSystem.streamingMessageIds,
    currentQuestionIndex: conversationState.currentQuestionIndex,

    // Settings
    botSettings: chatbotSettings.botSettings,
    isLoadingSettings: chatbotSettings.isLoading,
    settingsError: chatbotSettings.error,

    // Actions
    handleUserResponse,
    startConversation,
    resetConversation,
    getAIResponse,
    loadPreviousAnswers, // Expose for manual use when needed

    // Performance utilities
    getPerformanceSummary: () => performanceMonitor.getSummary(),
    clearPerformanceMetrics: () => performanceMonitor.clear()
  };
}
